import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { 
  AlertTriangle, TrendingUp, Shield, RefreshCw, Search, Users, 
  X, Target, FileText, Save, Building2, Star, ChevronLeft, ChevronRight, ArrowRightLeft,
  Lightbulb
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FilterPanel } from '@/components/FilterPanel';
import { AIPanel } from '@/components/AIPanel';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

const KRALJIC_COLORS = {
  Strategic: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  Leverage: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  Bottleneck: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  'Non-Critical': { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' }
};

export default function Suppliers() {
  const { symbol: cs } = useCurrency();
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [kraljicFilter, setKraljicFilter] = useState('all');
  
  // Standard data filters (managed by FilterPanel)
  const { filters: dataFilters, setFilters: setDataFilters } = useGlobalFilters();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  
  // Scorecard modal state
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [scorecardData, setScorecardData] = useState(null);
  const [savingScorecard, setSavingScorecard] = useState(false);
  
  // Editable scorecard fields
  const [evaluatedRisk, setEvaluatedRisk] = useState('Medium');
  const [qualityScore, setQualityScore] = useState(70);
  const [deliveryScore, setDeliveryScore] = useState(70);
  const [priceScore, setPriceScore] = useState(70);
  const [complianceScore, setComplianceScore] = useState(70);
  const [notes, setNotes] = useState('');
  const [modificationHistory, setModificationHistory] = useState([]);
  const [p2pProfile, setP2pProfile] = useState(null);
  const [p2pLoading, setP2pLoading] = useState(false);
  const [srmTip, setSrmTip] = useState(null);
  const [srmTipLoading, setSrmTipLoading] = useState(false);

  // Construct filter object for recommendations
  const activeFilters = useMemo(() => ({
    category: dataFilters.category || null,
    region: dataFilters.region || null,
    country: dataFilters.country || null,
  }), [dataFilters]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dataFilters.category_l2) params.append('category_l2', dataFilters.category_l2);
      if (dataFilters.category_l3) params.append('category_l3', dataFilters.category_l3);
      if (dataFilters.cost_center) params.append('cost_center', dataFilters.cost_center);
      if (dataFilters.business_unit) params.append('business_unit', dataFilters.business_unit);
      if (dataFilters.addressability) params.append('addressability', dataFilters.addressability);
      if (dataFilters.startDate) params.append('start_date', String(dataFilters.startDate).split('T')[0]);
      if (dataFilters.endDate) params.append('end_date', String(dataFilters.endDate).split('T')[0]);
      const qs = params.toString();
      const response = await api.get(`/suppliers${qs ? `?${qs}` : ''}`);
      const sorted = (response.data || []).sort((a, b) => (b.total_spend || 0) - (a.total_spend || 0));
      setSuppliers(sorted);
      setFilteredSuppliers(sorted);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dataFilters.category_l2, dataFilters.category_l3, dataFilters.cost_center, dataFilters.business_unit, dataFilters.addressability, dataFilters.startDate, dataFilters.endDate]);

  const filterSuppliers = useCallback(() => {
    let filtered = suppliers;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.name?.toLowerCase().includes(term) ||
        s.category?.toLowerCase().includes(term)
      );
    }

    if (riskFilter !== 'all') {
      filtered = filtered.filter(s => {
        const effectiveRisk = getEffectiveRisk(s);
        return effectiveRisk.toLowerCase() === riskFilter;
      });
    }

    if (dataFilters.category) {
      filtered = filtered.filter(s => s.category === dataFilters.category);
    }

    if (dataFilters.region) {
      filtered = filtered.filter(s => s.region === dataFilters.region);
    }

    if (dataFilters.country) {
      filtered = filtered.filter(s => s.country === dataFilters.country);
    }

    if (dataFilters.sbu) {
      filtered = filtered.filter(s => s.sbu === dataFilters.sbu);
    }

    if (kraljicFilter !== 'all') {
      filtered = filtered.filter(s => s.kraljic_status === kraljicFilter);
    }

    setFilteredSuppliers(filtered);
  }, [suppliers, searchTerm, riskFilter, dataFilters, kraljicFilter]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    filterSuppliers();
    setCurrentPage(1);
  }, [filterSuppliers]);

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setRiskFilter('all');
    setKraljicFilter('all');
    setDataFilters({
      category: null, category_l2: null, category_l3: null,
      region: null, country: null, sbu: null, fiscal_year: null,
    });
    setCurrentPage(1);
  };

  // Pagination logic
  const paginatedSuppliers = useMemo(() => {
    if (itemsPerPage >= filteredSuppliers.length) {
      return filteredSuppliers;
    }
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSuppliers.slice(start, start + itemsPerPage);
  }, [filteredSuppliers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage) || 1;

  const activeFilterCount = [
    searchTerm, riskFilter !== 'all' ? riskFilter : null, kraljicFilter !== 'all' ? kraljicFilter : null,
    ...Object.values(dataFilters),
  ].filter(v => v !== null && v !== '' && v !== 'all').length;

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSuppliers();
  };

  // Get effective risk - prioritizes evaluated_risk > risk_ratings (uploaded)
  const getEffectiveRisk = (supplier) => {
    if (supplier.evaluated_risk) return supplier.evaluated_risk;
    if (supplier.risk_ratings) return supplier.risk_ratings;
    return 'Not Rated';
  };

  // Get risk badge styling
  const getRiskBadge = (riskLevel) => {
    const level = (riskLevel || '').toLowerCase();
    if (level === 'high') return { label: 'High', variant: 'destructive', color: 'text-red-500' };
    if (level === 'medium') return { label: 'Medium', variant: 'default', color: 'text-yellow-500' };
    if (level === 'low') return { label: 'Low', variant: 'secondary', color: 'text-green-500' };
    return { label: 'Not Rated', variant: 'outline', color: 'text-muted-foreground' };
  };

  // Get Kraljic badge styling
  const getKraljicBadge = (status) => {
    return KRALJIC_COLORS[status] || KRALJIC_COLORS['Non-Critical'];
  };

  // Open scorecard modal
  const openScorecard = async (supplier) => {
    setSelectedSupplier(supplier);
    setScorecardOpen(true);
    setModificationHistory([]);
    setP2pProfile(null);
    setSrmTip(null);
    
    // Load scorecard + P2P profile in parallel
    const loadScorecard = api.get(`/suppliers/${supplier.id}/scorecard`).then(r => {
      setScorecardData(r.data);
      setEvaluatedRisk(r.data.evaluated_risk || supplier.risk_ratings || 'Medium');
      setQualityScore(r.data.quality_score || 70);
      setDeliveryScore(r.data.delivery_score || 70);
      setPriceScore(r.data.price_score || 70);
      setComplianceScore(r.data.compliance_score || 70);
      setNotes(r.data.scorecard_notes || '');
      setModificationHistory(r.data.modification_history || []);
      if (r.data.srm_tip) setSrmTip(r.data.srm_tip);
    }).catch(() => {
      setScorecardData(null);
      setEvaluatedRisk(supplier.risk_ratings || 'Medium');
      setQualityScore(70);
      setDeliveryScore(70);
      setPriceScore(70);
      setComplianceScore(70);
      setNotes('');
      setModificationHistory([]);
    });

    const loadP2p = (async () => {
      setP2pLoading(true);
      try {
        const r = await api.get(`/suppliers/${encodeURIComponent(supplier.name)}/p2p-profile`);
        setP2pProfile(r.data);
      } catch { setP2pProfile(null); }
      finally { setP2pLoading(false); }
    })();

    await Promise.all([loadScorecard, loadP2p]);
  };

  // Save scorecard
  const saveScorecard = async () => {
    if (!selectedSupplier) return;
    
    setSavingScorecard(true);
    try {
      const response = await api.post(`/suppliers/${selectedSupplier.id}/scorecard`, {
        evaluated_risk: evaluatedRisk,
        quality_score: qualityScore,
        delivery_score: deliveryScore,
        price_score: priceScore,
        compliance_score: complianceScore,
        notes: notes
      });
      
      // Add new modification entry to local state for immediate UI update
      if (response.data.modification_entry) {
        setModificationHistory(prev => [...prev, response.data.modification_entry]);
      }
      
      // Update supplier in local state for immediate badge update
      setSuppliers(prev => prev.map(s => 
        s.id === selectedSupplier.id 
          ? { ...s, evaluated_risk: evaluatedRisk }
          : s
      ));
      
      // Also update selectedSupplier state
      setSelectedSupplier(prev => ({ ...prev, evaluated_risk: evaluatedRisk }));
      
      toast.success('Scorecard saved successfully');
    } catch (error) {
      toast.error('Failed to save scorecard');
    } finally {
      setSavingScorecard(false);
    }
  };

  // Calculate overall score
  const calculateOverallScore = () => {
    return Math.round((qualityScore + deliveryScore + priceScore + complianceScore) / 4);
  };

  // Generate AI SRM Tip
  const generateSrmTip = async () => {
    if (!selectedSupplier) return;
    setSrmTipLoading(true);
    try {
      const response = await api.post(`/suppliers/${selectedSupplier.id}/srm-tip`, {
        kraljic_status: selectedSupplier.kraljic_status || 'Non-Critical',
        risk_level: evaluatedRisk || selectedSupplier.risk_ratings || 'Medium',
        quality_score: qualityScore,
        delivery_score: deliveryScore,
        price_score: priceScore,
        compliance_score: complianceScore,
        p2p_profile: p2pProfile,
      });
      setSrmTip(response.data);
      toast.success('SRM tip generated');
    } catch {
      toast.error('Failed to generate SRM tip');
    } finally {
      setSrmTipLoading(false);
    }
  };

  // Count suppliers by risk level - use filteredSuppliers for accurate filter-aware stats
  const highRiskCount = filteredSuppliers.filter(s => getEffectiveRisk(s).toLowerCase() === 'high').length;
  const mediumRiskCount = filteredSuppliers.filter(s => getEffectiveRisk(s).toLowerCase() === 'medium').length;
  const totalSpend = filteredSuppliers.reduce((sum, s) => sum + (s.total_spend || 0), 0);

  // Calculate average scorecard rating from filtered suppliers
  const suppliersWithScorecard = filteredSuppliers.filter(s => s.has_scorecard && s.scorecard_avg_rating);
  const avgScorecardRating = suppliersWithScorecard.length > 0 
    ? (suppliersWithScorecard.reduce((sum, s) => sum + s.scorecard_avg_rating, 0) / suppliersWithScorecard.length).toFixed(1)
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="supplier-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Supplier Management</h1>
          <p className="text-muted-foreground mt-1">Risk assessment and supplier performance tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="suppliers" />
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Standard Filters via FilterPanel */}
      <FilterPanel
        filters={dataFilters}
        onFiltersChange={(newFilters) => { setDataFilters(newFilters); setCurrentPage(1); }}
        pageName="suppliers"
        visibleFilters={['category', 'category_l2', 'category_l3', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        compact={true}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" />
              TOTAL SUPPLIERS
              <HelpBubble text="Distinct suppliers with at least one transaction. Reflects the breadth of your supplier base after applying the filters above." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{filteredSuppliers.length}</div>
            {filteredSuppliers.length !== suppliers.length && (
              <p className="text-xs text-muted-foreground mt-1">of {suppliers.length} total</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              HIGH RISK
              <HelpBubble text="Suppliers whose evaluated risk rating is High. Prioritise these for KYC refresh, mitigation plans, or dual-sourcing." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono text-red-500">{highRiskCount}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Shield className="w-4 h-4 text-yellow-500" />
              MEDIUM RISK
              <HelpBubble text="Suppliers rated Medium risk — monitor these for drift upward and ensure contracts carry standard safeguards." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono text-yellow-500">{mediumRiskCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              TOTAL SPEND
              <HelpBubble text="Total spend across all filtered suppliers, FX-normalised to your base currency." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold font-mono">{cs}{totalSpend.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Page-specific Filters */}
      <Card className="border-dashed" data-testid="suppliers-filter-panel">
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger data-testid="risk-filter">
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="high">High Risk</SelectItem>
                <SelectItem value="medium">Medium Risk</SelectItem>
                <SelectItem value="low">Low Risk</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kraljicFilter} onValueChange={setKraljicFilter}>
              <SelectTrigger data-testid="kraljic-filter">
                <SelectValue placeholder="Kraljic Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Kraljic Status</SelectItem>
                <SelectItem value="Strategic">Strategic</SelectItem>
                <SelectItem value="Leverage">Leverage</SelectItem>
                <SelectItem value="Bottleneck">Bottleneck</SelectItem>
                <SelectItem value="Non-Critical">Non-Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Suppliers List */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card>
          <CardHeader>
            <CardTitle>Suppliers ({filteredSuppliers.length})</CardTitle>
            <CardDescription>Click "View Scorecard" to assess and update risk ratings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {paginatedSuppliers.map((supplier) => {
                const uploadedRisk = getRiskBadge(supplier.risk_ratings);
                const effectiveRisk = getRiskBadge(getEffectiveRisk(supplier));
                const kraljic = getKraljicBadge(supplier.kraljic_status);
                
                return (
                  <div
                    key={supplier.id}
                    className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                    data-testid="supplier-row"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Supplier Info */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg">{supplier.name}</h3>
                          <p className="text-sm text-muted-foreground">{supplier.category || 'Uncategorized'}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {/* Effective Risk Badge */}
                            <Badge variant={effectiveRisk.variant} className="gap-1">
                              <Shield className="w-3 h-3" />
                              {effectiveRisk.label} Risk
                            </Badge>
                            
                            {/* Kraljic Status Badge */}
                            {supplier.kraljic_status && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${kraljic.bg} ${kraljic.text}`}>
                                <Target className="w-3 h-3" />
                                {supplier.kraljic_status}
                              </span>
                            )}
                            
                            {/* Always show Perceived Risk if evaluated_risk exists */}
                            {supplier.evaluated_risk && (
                              <span className="text-xs text-muted-foreground italic">
                                (Perceived: {supplier.evaluated_risk})
                              </span>
                            )}
                            {/* Show original risk rating if no evaluation done yet */}
                            {!supplier.evaluated_risk && supplier.risk_ratings && (
                              <span className="text-xs text-muted-foreground italic">
                                (Uploaded: {supplier.risk_ratings})
                              </span>
                            )}
                            
                            {/* External Risk Indicators */}
                            {supplier.external_risk && (
                              <div className="flex items-center gap-1.5">
                                {supplier.external_risk.credit_score != null && (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${supplier.external_risk.credit_score >= 70 ? 'bg-emerald-50 text-emerald-700' : supplier.external_risk.credit_score >= 40 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                                    Credit: {supplier.external_risk.credit_score}
                                  </span>
                                )}
                                {supplier.external_risk.esg_rating && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                                    ESG: {supplier.external_risk.esg_rating}
                                  </span>
                                )}
                                {supplier.external_risk.financial_health_score != null && (
                                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${supplier.external_risk.financial_health_score >= 70 ? 'bg-emerald-50 text-emerald-700' : supplier.external_risk.financial_health_score >= 40 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                                    Health: {supplier.external_risk.financial_health_score}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Spend & Rating */}
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Total Spend</p>
                          <p className="text-xl font-bold font-mono">{cs}{supplier.total_spend?.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Transactions</p>
                          <p className="text-xl font-bold font-mono">{supplier.transaction_count || 0}</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openScorecard(supplier)}
                          data-testid="view-scorecard-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View Scorecard
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredSuppliers.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No suppliers found</p>
              )}
            </div>
            
            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t">
              <div className="flex items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredSuppliers.length)} to {Math.min(currentPage * itemsPerPage, filteredSuppliers.length)} of {filteredSuppliers.length}
                </p>
                <Select 
                  value={itemsPerPage.toString()} 
                  onValueChange={(v) => { 
                    setItemsPerPage(v === 'all' ? filteredSuppliers.length : parseInt(v)); 
                    setCurrentPage(1); 
                  }}
                >
                  <SelectTrigger className="w-[140px]" data-testid="items-per-page">
                    <SelectValue placeholder="Per page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">12 per page</SelectItem>
                    <SelectItem value="24">24 per page</SelectItem>
                    <SelectItem value="48">48 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                    <SelectItem value="all">Show All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Scorecard Modal */}
      {/* AI Recommendations */}
      <AIPanel page="suppliers" filters={activeFilters} />

      {/* Scorecard Popup */}
      <AnimatePresence>
        {scorecardOpen && selectedSupplier && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setScorecardOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="scorecard-modal"
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-background border-b p-4 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Supplier Scorecard</h2>
                  <p className="text-sm text-muted-foreground">{selectedSupplier.name}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setScorecardOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {/* Risk Comparison Section */}
                <Card className="bg-muted/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Risk Assessment Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {/* Perceived Risk (from upload) */}
                      <div className="text-center p-3 rounded-lg bg-background">
                        <p className="text-xs text-muted-foreground mb-2">Perceived Risk</p>
                        <Badge variant={getRiskBadge(selectedSupplier.risk_ratings).variant}>
                          {selectedSupplier.risk_ratings || 'Not Provided'}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">From data file</p>
                      </div>
                      
                      {/* Kraljic Assessment */}
                      <div className="text-center p-3 rounded-lg bg-background">
                        <p className="text-xs text-muted-foreground mb-2">Kraljic Matrix</p>
                        {selectedSupplier.kraljic_status ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getKraljicBadge(selectedSupplier.kraljic_status).bg} ${getKraljicBadge(selectedSupplier.kraljic_status).text}`}>
                            <Target className="w-3 h-3" />
                            {selectedSupplier.kraljic_status}
                          </span>
                        ) : (
                          <Badge variant="outline">N/A</Badge>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">Spend/Volume analysis</p>
                      </div>
                      
                      {/* Evaluated Risk */}
                      <div className="text-center p-3 rounded-lg bg-primary/5 border-2 border-primary/20">
                        <p className="text-xs text-muted-foreground mb-2">Evaluated Risk</p>
                        <Badge variant={getRiskBadge(evaluatedRisk).variant}>
                          {evaluatedRisk}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">Your assessment</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* AI SRM Tip */}
                <div className="rounded-lg border border-border p-4 space-y-3" data-testid="srm-tip-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-semibold">AI SRM Recommendation</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={generateSrmTip}
                      disabled={srmTipLoading}
                      data-testid="generate-srm-tip-btn"
                      className="h-7 text-xs"
                    >
                      {srmTipLoading ? (
                        <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Generating...</>
                      ) : srmTip ? (
                        <><RefreshCw className="w-3 h-3 mr-1" />Regenerate</>
                      ) : (
                        <><TrendingUp className="w-3 h-3 mr-1" />Generate Tip</>
                      )}
                    </Button>
                  </div>
                  {srmTip ? (
                    <div data-testid="srm-tip-content">
                      <p className="text-sm font-medium mb-1">{srmTip.title}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{srmTip.content}</p>
                      <div className="flex items-center gap-2 pt-2">
                        <Badge variant="outline" className="text-[10px]">{srmTip.kraljic_status}</Badge>
                        <Badge variant="outline" className="text-[10px]">{srmTip.risk_level} Risk</Badge>
                        <span className="text-[10px] text-muted-foreground ml-auto">
                          {srmTip.generated_by} &middot; {new Date(srmTip.generated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic" data-testid="srm-tip-placeholder">
                      Click "Generate Tip" to get an AI-powered SRM recommendation based on this supplier's full profile.
                    </p>
                  )}
                </div>

                {/* Evaluated Risk Selection */}
                <div className="space-y-2">
                  <Label>Evaluated Risk Level</Label>
                  <Select value={evaluatedRisk} onValueChange={setEvaluatedRisk}>
                    <SelectTrigger data-testid="evaluated-risk-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low Risk</SelectItem>
                      <SelectItem value="Medium">Medium Risk</SelectItem>
                      <SelectItem value="High">High Risk</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">This will override the uploaded risk rating for this supplier</p>
                </div>

                {/* Performance Scores */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Performance Scores</h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Quality Score</Label>
                        <span className="text-sm font-mono">{qualityScore}%</span>
                      </div>
                      <Slider
                        value={[qualityScore]}
                        onValueChange={([v]) => setQualityScore(v)}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Delivery Score</Label>
                        <span className="text-sm font-mono">{deliveryScore}%</span>
                      </div>
                      <Slider
                        value={[deliveryScore]}
                        onValueChange={([v]) => setDeliveryScore(v)}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Price Competitiveness</Label>
                        <span className="text-sm font-mono">{priceScore}%</span>
                      </div>
                      <Slider
                        value={[priceScore]}
                        onValueChange={([v]) => setPriceScore(v)}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label>Compliance Score</Label>
                        <span className="text-sm font-mono">{complianceScore}%</span>
                      </div>
                      <Slider
                        value={[complianceScore]}
                        onValueChange={([v]) => setComplianceScore(v)}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Overall Score */}
                  <div className="p-4 rounded-lg bg-muted/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span className="font-medium">Overall Performance Score</span>
                    </div>
                    <span className="text-lg font-bold font-mono">{calculateOverallScore()}%</span>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Assessment Notes</Label>
                  <Textarea
                    placeholder="Add notes about this supplier's risk assessment..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Supplier Details */}
                <Card className="bg-muted/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Supplier Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total Spend</p>
                        <p className="font-mono font-semibold">{cs}{selectedSupplier.total_spend?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Transactions</p>
                        <p className="font-mono font-semibold">{selectedSupplier.transaction_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Category</p>
                        <p className="font-semibold">{selectedSupplier.category || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Invoices</p>
                        <p className="font-mono font-semibold">{selectedSupplier.invoice_count || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* P2P Process Profile */}
                {p2pLoading ? (
                  <Card className="bg-muted/20">
                    <CardContent className="py-6 flex items-center justify-center">
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading P2P profile...</span>
                    </CardContent>
                  </Card>
                ) : p2pProfile?.has_data && (
                  <Card className="bg-muted/20" data-testid="p2p-profile-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <ArrowRightLeft className="w-4 h-4 text-primary" />
                        P2P Process Profile
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-background text-center">
                          <p className="text-xs text-muted-foreground">POs</p>
                          <p className="text-lg font-bold font-mono" data-testid="p2p-supplier-pos">{p2pProfile.unique_pos}</p>
                          <p className="text-[10px] text-muted-foreground">{p2pProfile.po_coverage_pct}% coverage</p>
                        </div>
                        <div className="p-2 rounded-lg bg-background text-center">
                          <p className="text-xs text-muted-foreground">Invoices</p>
                          <p className="text-lg font-bold font-mono">{p2pProfile.unique_invoices}</p>
                        </div>
                        <div className="p-2 rounded-lg bg-background text-center">
                          <p className="text-xs text-muted-foreground">2-Way Match</p>
                          <p className={`text-lg font-bold font-mono ${p2pProfile.two_way_match_pct >= 90 ? 'text-emerald-600' : p2pProfile.two_way_match_pct >= 70 ? 'text-amber-600' : 'text-red-500'}`}>
                            {p2pProfile.two_way_match_pct}%
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-background text-center">
                          <p className="text-xs text-muted-foreground">3-Way Match</p>
                          <p className={`text-lg font-bold font-mono ${p2pProfile.three_way_match_pct >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {p2pProfile.three_way_match_pct}%
                          </p>
                        </div>
                        <div className="p-2 rounded-lg bg-background text-center">
                          <p className="text-xs text-muted-foreground">GRN Coverage</p>
                          <p className="text-lg font-bold font-mono">{p2pProfile.grn_coverage_pct}%</p>
                        </div>
                        <div className="p-2 rounded-lg bg-background text-center">
                          <p className="text-xs text-muted-foreground">Contracted</p>
                          <p className={`text-lg font-bold font-mono ${p2pProfile.contracted_pct >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {p2pProfile.contracted_pct}%
                          </p>
                        </div>
                      </div>
                      {p2pProfile.payment_terms?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <span className="text-[10px] text-muted-foreground mr-1">Payment Terms:</span>
                          {p2pProfile.payment_terms.map((t, i) => (
                            <Badge key={i} variant="outline" className="text-[10px]">{t.term} ({t.count})</Badge>
                          ))}
                        </div>
                      )}
                      {p2pProfile.buyers?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <span className="text-[10px] text-muted-foreground mr-1">Buyers:</span>
                          {p2pProfile.buyers.map((b, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px]">{b.buyer} ({b.count})</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Modification History */}
                {modificationHistory.length > 0 && (
                  <Card className="bg-muted/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Modification History</CardTitle>
                      <CardDescription>Audit trail of scorecard changes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {[...modificationHistory].reverse().map((entry, index) => (
                          <div 
                            key={index} 
                            className="p-3 rounded-lg bg-background border text-sm"
                            data-testid="modification-history-entry"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant={getRiskBadge(entry.evaluated_risk).variant} className="text-xs">
                                  {entry.evaluated_risk}
                                </Badge>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">Score: {Math.round((entry.quality_score + entry.delivery_score + entry.price_score + entry.compliance_score) / 4)}%</span>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              <p>By {entry.user_name || entry.user_email} • {new Date(entry.timestamp).toLocaleString()}</p>
                              {entry.notes && (
                                <p className="mt-1 italic text-foreground/70">"{entry.notes}"</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-background border-t p-4 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setScorecardOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={saveScorecard} disabled={savingScorecard} data-testid="save-scorecard-btn">
                  {savingScorecard ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Scorecard
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
